import { createContext, useContext, useEffect, useRef } from "react";

/** Receives what the person typed into the palette when no command fits ("add a chart of margin by region"). */
export type FreeTextHandler = (text: string) => void | Promise<void>;

export interface CommandFreeTextApi {
  /** The registered handler, or null: the palette then hides its "Describe a change" row. */
  handler: FreeTextHandler | null;
  /** Registers a handler; the returned function removes it (only if it is still the registered one). */
  register: (handler: FreeTextHandler) => () => void;
  /** Where the palette and scrim start from the left (a CSS length); undefined = the whole window. */
  insetLeft?: string;
  /** Registers the inset; the returned function clears it (only if it is still the registered one). */
  registerInset?: (insetLeft: string) => () => void;
}

const NONE: CommandFreeTextApi = { handler: null, register: () => () => undefined };

export const CommandFreeTextContext = createContext<CommandFreeTextApi>(NONE);

export const useCommandFreeText = (): CommandFreeTextApi => useContext(CommandFreeTextContext);

/**
 * Lets a feature take free text from the palette. Pass `onFreeText` while the feature can act on it, and
 * nothing (or null) while it cannot. The latest function is always the one called.
 */
export const useRegisterFreeText = (onFreeText?: FreeTextHandler | null): void => {
  const { register } = useCommandFreeText();
  const latest = useRef(onFreeText);
  latest.current = onFreeText;
  const active = Boolean(onFreeText);
  useEffect(() => {
    if (!active) return undefined;
    return register((text) => latest.current?.(text));
  }, [active, register]);
};

/**
 * Lets a page keep the palette's scrim off a column it should not cover (the Data app tab's chat column).
 * Pass the left edge as a CSS length while it applies, and nothing while it does not.
 */
export const useRegisterPaletteInset = (insetLeft?: string): void => {
  const { registerInset } = useCommandFreeText();
  useEffect(() => {
    if (!insetLeft || !registerInset) return undefined;
    return registerInset(insetLeft);
  }, [insetLeft, registerInset]);
};
